import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { DjangoService } from '@core/services/django.service';
import { PlausibleService } from '@core/services/plausible.service';
import { ReferralService } from '@core/services/referral.service';
import { PublicUser } from '@models/public-user.model';
import { User } from '@models/user.model';
import { concat, Observable, of, timer } from 'rxjs';
import {
    catchError,
    map,
    switchMap,
    switchMapTo,
    take,
    tap,
} from 'rxjs/operators';
import { TokenStorageService } from './token-storage.service';

export enum UserGroups {
    salesPerson = 'Sales',
    tripManager = 'Trip Manager',
    paymentsAdmin = 'Payments Admin',
    bookingAdmin = 'Booking Admin',
    editor = 'Editor',
    preview = 'Preview',
    localLeader = 'Local Leader',
}

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private cache: Observable<User>;
    private readonly authenticationObservable = this.tokenStorageService
        .getToken()
        .pipe(map((token: string) => !!token));

    constructor(
        private djangoService: DjangoService,
        private tokenStorageService: TokenStorageService,
        private referralService: ReferralService,
        private plausibleService: PlausibleService
    ) {}

    create(
        firstname: string,
        lastname: string,
        email: string,
        phonenumber: string = null,
        profile_picture: string = null
    ) {
        const user = new User();
        user.firstname = firstname;
        user.lastname = lastname;
        user.email = email;
        user.phonenumber = phonenumber;
        user.profilePicture = profile_picture;
        return user;
    }

    register(user: User, password1: string, password2: string) {
        const data = user.serialize();
        data['password1'] = password1;
        data['password2'] = password2;
        data['referred_by'] = this.referralService.getReferralCode();
        return this.djangoService
            .register(data)
            .pipe(map(data => this.handleLoginData(data)));
    }

    validateEmailExists({
        value,
    }: AbstractControl): Observable<ValidationErrors | null> {
        const observable = this.djangoService.emailExists(value).pipe(take(1));
        return timer(1000).pipe(
            switchMapTo(observable),
            catchError(error => of(null)),
            map(data => {
                if (!data) {
                    return null;
                } else {
                    return { emailExists: true };
                }
            })
        );
    }

    saveLocation(country: string, city: string) {
        const data = {};
        if (country) {
            data['country'] = country;
        }
        if (city) {
            data['city'] = city;
        }
        return this.updateUser(data);
    }

    updateUser(inputData: any) {
        const backendData = {};
        for (let [key, value] of Object.entries(inputData)) {
            backendData[User.mapField(key)] = value || null;
        }
        this.djangoService.clear_cache();
        return this.djangoService.updateUser(backendData);
    }

    saveProfilePicture(imageData: string) {
        const data = {
            profile_picture: imageData,
        };
        this.djangoService.clear_cache();
        return this.djangoService.uploadProfilePicture(data);
    }

    fetch_user(): Observable<User> {
        return this.djangoService.user().pipe(
            map(data => new User().deserialize(data)),
            tap(user => {
                if (user.isStaff) {
                    this.plausibleService.disable();
                }
            })
        );
    }

    userVerified(): Observable<boolean> {
        return concat(
            of(true),
            this.fetch_user().pipe(map((user: User) => user.verified))
        );
    }

    profile(referralCode: string): Observable<PublicUser> {
        return this.djangoService
            .profile(referralCode)
            .pipe(map(data => new PublicUser().deserialize(data)));
    }

    resendVerificationEmail() {
        return this.djangoService.resendVerificationEmail();
    }

    login(email: string, password: string) {
        const data = {
            email: email,
            password: password,
        };
        return this.djangoService
            .login(data)
            .pipe(map(data => this.handleLoginData(data)));
    }

    logout() {
        return this.isAuthenticated().pipe(
            switchMap(isAuthenticated =>
                isAuthenticated ? this.djangoService.logout() : of(null)
            ),
            tap(() => this.tokenStorageService.removeTokens())
        );
    }

    public requestPasswordReset(email: string): Observable<any> {
        return this.djangoService.requestPasswordReset({ email: email });
    }

    public resetPassword(
        uid: string,
        token: string,
        password1: string,
        password2: string
    ) {
        const data = {
            uid: uid,
            token: token,
            new_password1: password1,
            new_password2: password2,
        };
        return this.djangoService.confirmPasswordReset(data);
    }

    public isAuthenticated(): Observable<boolean> {
        return this.authenticationObservable.pipe(take(1));
    }

    public isStaff(): Observable<boolean> {
        const isStaff = this.fetch_user().pipe(
            map((user: User) => user.isStaff)
        );
        return this.isAuthenticated().pipe(
            switchMap(isAuthenticated =>
                isAuthenticated ? isStaff : of(false)
            )
        );
    }

    public isVerified(): Observable<boolean> {
        const isVerified = this.fetch_user().pipe(
            map((user: User) => user.verified)
        );
        return this.isAuthenticated().pipe(
            switchMap(isAuthenticated =>
                isAuthenticated ? isVerified : of(false)
            )
        );
    }

    public hasGroup(group: string): Observable<boolean> {
        const hasGroup = this.fetch_user().pipe(
            map((user: User) => user.groups.includes(group))
        );
        return this.isAuthenticated().pipe(
            switchMap(isAuthenticated =>
                isAuthenticated ? hasGroup : of(false)
            )
        );
    }

    public hasAnyGroup(...groups: UserGroups[]): Observable<UserGroups[]> {
        const hasGroup = this.fetch_user().pipe(
            map((user: User) =>
                groups.filter(group => user.groups.includes(group))
            )
        );
        return this.isAuthenticated().pipe(
            switchMap(isAuthenticated => (isAuthenticated ? hasGroup : of([])))
        );
    }

    private clearCache() {
        this.cache = null;
    }

    private handleLoginData(data: any) {
        this.tokenStorageService.storeTokens(data.access, data.refresh);
        return new User().deserialize(data.user);
    }
}
